import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBolt } from '@fortawesome/pro-light-svg-icons/faBolt';
import { CatalogSuggestionSource } from 'fym-shared/src/smartlinks/catalog';
import { TypedMemo } from '../../common/types';
import { Panel, PanelHead } from '../../music24/components/primitives/Panel';
import { CatalogSuggestion } from '../../../redux/api/fymAPI.types';
import { SuggestionRow } from './SuggestionRow';

interface SuggestionsPanelProps {
  suggestions?: CatalogSuggestion[];
  onDismiss: (catalogTrackMaid: string) => void;
  onClaim: (suggestion: CatalogSuggestion) => void;
  onMerge: (suggestion: CatalogSuggestion) => void;
  onNotTheSame: (catalogTrackMaid: string) => void;
  /** The suggestion whose merge runs, if any. */
  busyMaid?: string;
}

/**
 * "Possibly yours" — releases read off the artist's own profiles, then catalog
 * tracks credited to a name close to the account's.
 *
 * The panel is absent rather than empty: an artist with nothing to review
 * should not be shown a box that says so. A search outage answers with an empty
 * list, and the same rule hides it.
 */
const SuggestionsPanelComponent: React.FC<SuggestionsPanelProps> = ({
  suggestions,
  onDismiss,
  onClaim,
  onMerge,
  onNotTheSame,
  busyMaid,
}) => {
  const intl = useIntl();

  const hasProfileRows = useMemo(
    () => (suggestions ?? []).some((suggestion) => suggestion.source === CatalogSuggestionSource.profile),
    [suggestions]
  );

  if (suggestions === undefined || suggestions.length === 0) {
    return null;
  }

  return (
    <Panel>
      <PanelHead
        icon={<FontAwesomeIcon icon={faBolt} />}
        title={intl.formatMessage({ id: 'app.sml.catalog.suggestions.title', defaultMessage: 'Possibly yours' })}
        right={
          hasProfileRows
            ? intl.formatMessage(
                {
                  id: 'app.sml.catalog.suggestions.count_with_profile',
                  defaultMessage: 'from your profiles and your name · {count}',
                },
                { count: suggestions.length }
              )
            : intl.formatMessage(
                {
                  id: 'app.sml.catalog.suggestions.count',
                  defaultMessage: 'matched on artist name · {count}',
                },
                { count: suggestions.length }
              )
        }
      />
      {suggestions.map((suggestion) => (
        <SuggestionRow
          key={suggestion.catalogTrackMaid}
          suggestion={suggestion}
          onDismiss={onDismiss}
          onClaim={onClaim}
          onMerge={onMerge}
          onNotTheSame={onNotTheSame}
          isBusy={busyMaid === suggestion.catalogTrackMaid}
        />
      ))}
    </Panel>
  );
};

export const SuggestionsPanel = TypedMemo(SuggestionsPanelComponent);
